import React, { useMemo } from "react";
import { useTranslations } from "next-intl";

import { Button, Modal } from "@/components/common";
import PaymentForm from "../payment-form/payment-form";
import { InvoiceIcon, SmileIcon } from "@/components/assets/icons";
import { useModal } from "@/hooks/useModal";
import { useParcelsRequest } from "@/api/useParcelsRequest";
import { ButtonColor, Theme } from "@/enums";
import { IShipment } from "@/types/apiResponse.types";
import { unpaidParcelStatuses } from "@/utils/unpaidParcelStatuses";
import { useGetAllFromCountriesRequest } from "@/api/useGetAllFromCountriesRequest";
import { useTheme } from "@/context/Theme.context";
import { formatNumber } from "@/utils/formatNumber";

export interface IUnpaidParcelsData {
  unpaidParcels: IShipment[];
  totalAmount: number;
  countByCountry: Record<string, number>;
}

const UpcomingBills = () => {
  const t = useTranslations();
  const theme = useTheme();

  const { modalOpen, openModal, closeModal } = useModal();

  const { data, isLoading } = useParcelsRequest({
    isPaid: false,
  });

  const { data: countriesData } = useGetAllFromCountriesRequest();
  const countriesList = countriesData?.data?.data || [];

  const priceField = theme === Theme.UZ ? "priceNational" : "priceUsd";

  const unpaidParcelsData = useMemo<IUnpaidParcelsData>(() => {
    const parcelsData = data?.data?.data;

    if (!parcelsData)
      return {
        unpaidParcels: [],
        totalAmount: 0,
        countByCountry: countriesList.reduce(
          (acc: Record<string, number>, country) => {
            acc[country.countryName.toLowerCase()] = 0;
            return acc;
          },
          {},
        ),
      };

    const unpaidParcels = parcelsData.filter((parcel) =>
      unpaidParcelStatuses.includes(parcel.commonStatusCode),
    );

    const countByCountry = countriesList.reduce(
      (acc: Record<string, number>, country) => {
        acc[country.countryName.toLowerCase()] = unpaidParcels.filter(
          (parcel) => parcel.countryId === country.countryId,
        ).length;
        return acc;
      },
      {},
    );
    return {
      unpaidParcels,
      totalAmount: unpaidParcels.reduce(
        (acc, item) => acc + item[priceField],
        0,
      ),
      countByCountry,
    };
  }, [data, countriesList]);

  return (
    <>
      <div className="flex w-full flex-row items-center justify-between gap-6 rounded-3xl bg-white px-[1.875rem] py-8 lg:h-[133px]">
        {!isLoading && (
          <>
            {unpaidParcelsData?.unpaidParcels?.length > 0 ? (
              <>
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-[#FDF3F3] p-6">
                  <InvoiceIcon />
                </div>
                <div className="flex w-full flex-row justify-between gap-4">
                  <div className="flex flex-col">
                    <h1 className="text-xl font-bold text-neutral-900">
                      {unpaidParcelsData?.unpaidParcels?.length || 0}{" "}
                      {t("upcoming_bills")}
                    </h1>
                    <p className="mt-2 text-sm font-normal text-zinc-600">
                      {countriesList
                        .map((country) => {
                          const key = country.countryName.toLowerCase();
                          const count =
                            unpaidParcelsData?.countByCountry?.[key] || 0;
                          return `${count} ${t(country.countryName)}`;
                        })
                        .join(" • ")}
                    </p>
                  </div>
                  <Button
                    onClick={openModal}
                    color={ButtonColor.DANGER}
                    className="h-[44px] max-w-fit"
                  >
                    {t("pay")}{" "}
                    {formatNumber(
                      Number(unpaidParcelsData?.totalAmount || 0).toFixed(2),
                    )}{" "}
                    {theme === Theme.UZ ? t("uzs") : t("usd")}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-[#EEF4EE] p-6">
                  <SmileIcon />
                </div>
                <div className="flex w-full flex-row gap-4">
                  <div className="flex flex-col">
                    <h1 className="text-xl font-bold text-neutral-900">
                      {t("you_dont_have_upcoming_bills")}
                    </h1>
                    <p className="mt-2 text-sm font-normal text-zinc-600">
                      {t("you_look_good")}
                    </p>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
      <Modal
        title="payments"
        isOpen={modalOpen}
        closeModal={closeModal}
        showCloseIcon
        paddings="!p-0"
      >
        <PaymentForm closeModal={closeModal} />
      </Modal>
    </>
  );
};

export default UpcomingBills;
